import { useState, useRef, useEffect } from 'react';
import Edit2Icon from './icons/Edit2Icon';
import Trash2Icon from './icons/Trash2Icon';
import PlusIcon from './icons/PlusIcon';
import LinkIcon from './icons/LinkIcon';
import VerticalEllipsisIcon from './icons/VerticalEllipsisIcon';

interface CardActionDropdownProps {
  onEdit: () => void;
  onDelete: () => void;
  onCreateLinkedCard?: () => void;
  onLinkExistingCard?: () => void;
}

export function CardActionDropdown({
  onEdit,
  onDelete,
  onCreateLinkedCard,
  onLinkExistingCard,
}: CardActionDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleEdit = () => {
    setIsOpen(false);
    onEdit();
  };

  const handleDelete = () => {
    setIsOpen(false);
    onDelete();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-gray-400 hover:text-white hover:bg-gray-700 p-2 rounded-lg transition-colors"
        title="Card actions"
        aria-label="Card actions"
        aria-expanded={isOpen}
      >
        <VerticalEllipsisIcon />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-56 bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50">
          <div className="py-1">
            <button
              onClick={handleEdit}
              className="w-full flex items-center gap-2 px-4 py-2 text-left text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
            >
              <span className="w-4 h-4">
                <Edit2Icon />
              </span>
              Edit Card
            </button>
            {onCreateLinkedCard && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  onCreateLinkedCard();
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
              >
                <span className="w-4 h-4">
                  <LinkIcon />
                </span>
                Create Linked Card
              </button>
            )}
            {onLinkExistingCard && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  onLinkExistingCard();
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
              >
                <span className="w-4 h-4">
                  <PlusIcon />
                </span>
                Link Existing Card
              </button>
            )}
            <button
              onClick={handleDelete}
              className="w-full flex items-center gap-2 px-4 py-2 text-left text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-colors"
            >
              <span className="w-4 h-4">
                <Trash2Icon />
              </span>
              Delete Card
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
